import React from 'react';
import { Button } from '@vehix/portal/components/ui/button';
import { Input } from '@vehix/portal/components/ui/input';
import { Label } from '@vehix/portal/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@vehix/portal/components/ui/select';
import { slotLabel, type Slot } from '../../utils/scheduleSlots';

const DAY_LABELS: ReadonlyArray<{ key: string; label: string; index: number }> = [
  { key: 'sun', label: 'Sun', index: 0 },
  { key: 'mon', label: 'Mon', index: 1 },
  { key: 'tue', label: 'Tue', index: 2 },
  { key: 'wed', label: 'Wed', index: 3 },
  { key: 'thu', label: 'Thu', index: 4 },
  { key: 'fri', label: 'Fri', index: 5 },
  { key: 'sat', label: 'Sat', index: 6 }
];

const DAY_PRESETS: ReadonlyArray<{ label: string; days: readonly number[] }> = [
  // Sun + Wed first: it is what every live schedule actually uses.
  { label: 'Sun + Wed', days: [0, 3] },
  { label: 'Every day', days: [0, 1, 2, 3, 4, 5, 6] },
  { label: 'Weekdays', days: [1, 2, 3, 4, 5] }
];

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'UTC', label: 'UTC' }
];

/** What the chosen time reads as on the dealership's own clock. */
function dealerReading(
  hour: number,
  minute: number,
  scheduleZone: string,
  dealerZone: string
): string | null {
  if (scheduleZone === dealerZone) return null;
  // Any date works — only the zone difference matters, and a mid-year one
  // avoids straddling a DST boundary between the two zones.
  const base = new Date(Date.UTC(2026, 5, 15, 12, 0));
  const asSchedule = new Date(
    base.getTime() + (hour * 60 + minute - zoneMinutes(base, scheduleZone)) * 60_000
  );
  return formatIn(asSchedule, dealerZone);
}

function zoneMinutes(at: Date, zone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(at);
  const h = Number(parts.find(p => p.type === 'hour')?.value) % 24;
  const m = Number(parts.find(p => p.type === 'minute')?.value);
  return h * 60 + m;
}

function formatIn(at: Date, zone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(at);
}

interface SlotAdviceProps {
  suggestions: Slot[];
  chosenSlot: Slot | null;
  scheduleZone: string;
  dealerZone: string;
  dealerName?: string | undefined;
  onPick: (hour: number, minute: number) => void;
}

/**
 * Suggested times, and what the chosen one means where the dealer is.
 *
 * Replaces a next-runs list. The useful question at authoring time isn't "what
 * dates will this hit" — it's "is this hour safe and sensible", which needs the
 * dealer's clock and the other schedules, not a calendar.
 */
const SlotAdvice: React.FC<SlotAdviceProps> = ({
  suggestions,
  chosenSlot,
  scheduleZone,
  dealerZone,
  dealerName,
  onPick
}) => {
  if (suggestions.length === 0 && !chosenSlot) return null;

  const local =
    chosenSlot && dealerReading(chosenSlot.hour, chosenSlot.minute, scheduleZone, dealerZone);

  return (
    <div className='space-y-1.5'>
      {local && (
        <p className='text-[11px] text-muted-foreground'>
          That&apos;s <span className='font-medium'>{local}</span> at{' '}
          {dealerName ?? 'the dealership'}.
        </p>
      )}

      {suggestions.length > 0 && (
        <div className='space-y-1'>
          <p className='text-[10px] font-semibold uppercase tracking-wide text-muted-foreground'>
            Suggested — closed, and done before they open
          </p>
          <div className='flex flex-wrap gap-1'>
            {suggestions.map(s => (
              <button
                key={`${s.hour}:${s.minute}`}
                type='button'
                onClick={() => onPick(s.hour, s.minute)}
                title={
                  s.nearestOemGapMinutes === Infinity
                    ? 'Nothing else crawls these brands'
                    : `Nearest same-brand run is ${s.nearestOemGapMinutes} min away`
                }
                className='rounded border bg-background px-1.5 py-0.5 text-[11px] hover:bg-muted'
              >
                {slotLabel(s)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface ScheduleWhenSectionProps {
  customCron: string | null;
  setCustomCron: (v: string | null) => void;
  scheduleHour: number;
  setScheduleHour: (v: number) => void;
  scheduleMinute: number;
  setScheduleMinute: (v: number) => void;
  daysOfWeek: number[];
  setDaysOfWeek: (v: number[]) => void;
  onToggleDay: (day: number) => void;
  timeZone: string;
  setTimeZone: (v: string) => void;
  error?: string | undefined;
  /** Best available times, already ranked. Empty until zones are picked. */
  suggestions: Slot[];
  /** How the currently chosen time scores, for the inline warning. */
  chosenSlot: Slot | null;
  /** The dealership's own zone, for the "what time is that there" line. */
  dealerZone: string;
  dealerName?: string | undefined;
}

/**
 * When the schedule fires: time, timezone, and days.
 *
 * Time and timezone sit side by side deliberately — they were separated by the
 * day grid before, which is how a schedule ends up at the right hour in the
 * wrong zone. Underneath, suggested times and the dealer's own reading of the
 * chosen hour answer the question that actually matters here: is this a safe
 * time to run.
 */
// eslint-disable-next-line max-lines-per-function
export const ScheduleWhenSection: React.FC<ScheduleWhenSectionProps> = ({
  customCron,
  setCustomCron,
  scheduleHour,
  setScheduleHour,
  scheduleMinute,
  setScheduleMinute,
  setDaysOfWeek,
  daysOfWeek,
  onToggleDay,
  timeZone,
  setTimeZone,
  error,
  suggestions,
  chosenSlot,
  dealerZone,
  dealerName
}) => (
  <div className='space-y-3'>
    <Label>Schedule</Label>

    {customCron ? (
      <div className='rounded-md border bg-muted/30 px-3 py-2 text-xs space-y-1'>
        <div className='font-medium'>Custom schedule</div>
        <div className='flex items-center gap-2'>
          <code className='font-mono'>{customCron}</code>
          <button
            type='button'
            onClick={() => {
              setCustomCron(null);
              setScheduleHour(2);
              setScheduleMinute(0);
              setDaysOfWeek([0, 1, 2, 3, 4, 5, 6]);
            }}
            className='text-primary underline'
          >
            Switch to time/day picker
          </button>
        </div>
        <p className='text-muted-foreground'>
          This schedule uses a cron pattern the picker can&apos;t represent. Edits will send the raw
          expression unchanged.
        </p>
      </div>
    ) : (
      <>
        {/* Time sits next to its timezone. They were ~100px apart with
          the day grid between them, which is how a schedule ends up
          set to the right hour in the wrong zone. */}
        <div className='grid grid-cols-2 gap-3'>
          <div className='space-y-1'>
            <Label className='text-[11px] text-muted-foreground'>Time</Label>
            <Input
              type='time'
              value={`${String(scheduleHour).padStart(2, '0')}:${String(scheduleMinute).padStart(
                2,
                '0'
              )}`}
              onChange={e => {
                const parts = e.target.value.split(':');
                const h = Number(parts[0]);
                const m = Number(parts[1]);
                if (Number.isFinite(h)) setScheduleHour(h);
                if (Number.isFinite(m)) setScheduleMinute(m);
              }}
            />
          </div>
          <div className='space-y-1'>
            <Label className='text-[11px] text-muted-foreground'>Timezone</Label>
            <Select value={timeZone} onValueChange={setTimeZone}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONE_OPTIONS.map(tz => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className='space-y-1'>
          <Label className='text-[11px] text-muted-foreground'>Days of week</Label>
          <div className='flex gap-1'>
            {DAY_LABELS.map(({ key, label, index }) => {
              const active = daysOfWeek.includes(index);
              const cls = active
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background hover:bg-muted';
              const baseCls =
                'flex-1 rounded-md border py-1.5 text-xs font-medium transition-colors';
              return (
                <button
                  key={key}
                  type='button'
                  onClick={() => onToggleDay(index)}
                  className={`${baseCls} ${cls}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div className='flex gap-1 pt-1'>
            {DAY_PRESETS.map(preset => (
              <Button
                key={preset.label}
                type='button'
                size='sm'
                variant='outline'
                className='h-7 flex-1 text-xs'
                onClick={() => setDaysOfWeek([...preset.days])}
              >
                {preset.label}
              </Button>
            ))}
          </div>
        </div>

        <SlotAdvice
          suggestions={suggestions}
          chosenSlot={chosenSlot}
          scheduleZone={timeZone}
          dealerZone={dealerZone}
          dealerName={dealerName}
          onPick={(h, m) => {
            setScheduleHour(h);
            setScheduleMinute(m);
          }}
        />
      </>
    )}
    {error && <p className='text-xs text-red-500'>{error}</p>}
  </div>
);
